import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { Target, Eye, LogIn, MapPin, Gift, BookOpen } from "lucide-react";
import { SetupGuideDialog } from "@/components/admin/SetupGuideDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [liveEvents, setLiveEvents] = useState([]);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    axios.get(`${API}/venues/active`).then((r) => setLiveEvents(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://static.prod-images.emergentagent.com/jobs/f707a822-bbe1-4c69-96b7-9259b8de4d00/images/fbdf8f044500ad2fe4d88a49bf647ff10fc9a081bcebea33ee50a27fb0f33e44.png)",
        }}
      />
      <div className="absolute inset-0 bg-black/70" />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <div className="animate-fade-in">
          <Target className="w-14 h-14 text-[#E3292E] mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl text-white uppercase tracking-tighter" data-testid="app-title">
            Darts Manager
          </h1>
          <p className="text-base sm:text-lg text-white/60 mt-4 font-body max-w-md mx-auto">
            Your Darts Events Manager
          </p>
          <p className="text-sm text-[#309F6A] mt-2 font-body flex items-center justify-center gap-1.5" data-testid="free-credit-badge">
            <Gift className="w-4 h-4" /> 1 Free Credit on Signup
          </p>
        </div>

        {liveEvents.length > 0 && (
          <div className="mt-10 w-full max-w-lg space-y-3 animate-slide-up" data-testid="live-events-section">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E] mb-2">
              {liveEvents.length} Active Event{liveEvents.length !== 1 ? "s" : ""}
            </p>
            {liveEvents.map((ev, i) => (
              <button
                key={`${ev.venue_id}-${i}`}
                onClick={() => navigate(`/scoreboard?v=${ev.venue_id}`)}
                className="w-full border border-white/10 bg-black/60 backdrop-blur-sm p-4 text-left hover:border-[#E3292E]/40 hover:bg-black/70 transition-colors group"
                data-testid={`live-event-${i}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge className={`rounded-none text-[10px] tracking-[0.15em] uppercase font-bold px-1.5 py-0 ${ev.status === "active" ? "bg-[#E3292E] text-white" : "bg-white/20 text-white/70"}`}>
                        {ev.status === "active" ? "LIVE" : "SETTING UP"}
                      </Badge>
                      <span className="text-white font-heading font-bold text-lg">{ev.event_name}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 text-white/40 text-sm font-body">
                      <MapPin className="w-3 h-3" />
                      <span>{ev.venue_name}</span>
                      <span className="mx-1">&middot;</span>
                      <span className="capitalize">{ev.game_format} {ev.play_mode}</span>
                    </div>
                  </div>
                  <Eye className="w-4 h-4 text-white/20 group-hover:text-[#E3292E] transition-colors" />
                </div>
              </button>
            ))}
          </div>
        )}

        <div className={`${liveEvents.length > 0 ? "mt-8" : "mt-10"} flex flex-col sm:flex-row gap-4 animate-slide-up`} style={{ animationDelay: "0.15s" }}>
          <Button
            onClick={() => navigate("/scoreboard")}
            className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none px-8 py-6 text-base font-heading font-bold tracking-wider transition-colors duration-200"
            data-testid="view-scoreboard-btn"
          >
            <Eye className="mr-2 h-5 w-5" /> VIEW SCOREBOARD
          </Button>
          <Button
            onClick={() => navigate("/admin/login")}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10 rounded-none px-8 py-6 text-base font-heading font-bold tracking-wider transition-colors duration-200"
            data-testid="admin-login-btn"
          >
            <LogIn className="mr-2 h-5 w-5" /> ADMIN LOGIN
          </Button>
        </div>

        <button
          onClick={() => setShowGuide(true)}
          className="mt-6 flex items-center gap-2 text-white/35 hover:text-white/70 transition-colors duration-200 animate-slide-up"
          style={{ animationDelay: "0.3s" }}
          data-testid="landing-guide-btn"
        >
          <BookOpen className="w-4 h-4" />
          <span className="text-sm font-body tracking-wide">How It Works — Quick Start Guide</span>
        </button>

        <SetupGuideDialog open={showGuide} onOpenChange={setShowGuide} />
      </div>
    </div>
  );
}
